import { useMemo } from "react";
import { useAppSelector } from "../../../store";
import { ActionReports, Report } from "../../../common/interfaces/config";
import { ROUTE_PATHS } from "../../../common/enums/routing-enums";
import { hasSSIProgramActive } from "../../../common/util/Navigation/checkReportEligibility";

/**
 * Sort action reports by market preference and add additional pages
 * @returns {actionReportMenuOptions}
 */
export const useActionReportMenuOptions = () => {
  const { configuration, shellConfig } = useAppSelector((state) => state.boot);

  const sortReportsByMarketPreference = (actionReports: ActionReports) => {
    const { reportMenuSequence, reports } = actionReports;
    return reports
      .slice()
      .sort((reportA: Report, reportB: Report) => reportMenuSequence[reportA.id] - reportMenuSequence[reportB.id]);
  };

  const actionReportMenuOptions = useMemo(() => {
    if (!configuration.actionReports) {
      return [];
    }

    const sortedReports = sortReportsByMarketPreference(configuration.actionReports);

    const showWhosNewIncludesCustomers = configuration.actionReports.showWhosNewIncludesCustomers ?? false;
    const reportsWithContentKeys = showWhosNewIncludesCustomers
      ? sortedReports.map((item) => (item.id === "whosNew" ? { ...item, contentKey: "whosNewWithCustomers" } : item))
      : sortedReports;

    const additionalPages: Report[] = [];

    // SSI Upline: Requires market config AND enrollment (matches backend Group Management API logic)
    if (hasSSIProgramActive(shellConfig.eligibleIncentives, configuration.navigation?.showSSIUpline ?? false)) {
      additionalPages.push({ id: "ssiUplineReport", url: "/ssi-upline-report.html" });
    }

    if (configuration.navigation?.showFsiActionReport ?? false) {
      additionalPages.push({ id: "fsiActionReport", url: ROUTE_PATHS.FSI_ACTION_REPORT });
    }

    if (configuration.navigation?.showBronzeGroupTracking ?? false) {
      additionalPages.push({ id: "bronzeGroupTracking", url: "/bronze-group-tracking" });
    }

    if (configuration.navigation?.showGroupSopTracking ?? false) {
      additionalPages.push({ id: "groupSopTracking", url: ROUTE_PATHS.SOP_REPORT });
    }

    return [...reportsWithContentKeys, ...additionalPages];
  }, [configuration, shellConfig]);

  return { actionReportMenuOptions };
};
